import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  FolderOpen,
} from "lucide-react";

const baseButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  padding: "0.45rem 0.9rem",
  borderRadius: "12px",
  backgroundColor: "#111113",
  border: "1px solid #2b2b2f",
  color: "#e5e7eb",
  fontSize: "13px",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.2s ease",
};

const dropdownStyle = {
  position: "absolute",
  marginTop: "0.5rem",
  right: 0,
  width: "220px",
  borderRadius: "14px",
  backgroundColor: "#0d0d0f",
  border: "1px solid #2b2b2f",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.55)",
  overflow: "hidden",
  zIndex: 4000,
};

const optionButtonStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.6rem 1rem",
  backgroundColor: "transparent",
  border: "none",
  color: "#f4f4f5",
  fontSize: "13px",
  cursor: "pointer",
  transition: "background 0.15s ease, color 0.15s ease",
  textAlign: "left",
};

const footerButtonStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.6rem 1rem",
  fontSize: "12px",
  color: "#a1a1aa",
  backgroundColor: "transparent",
  border: "none",
  cursor: "pointer",
  transition: "color 0.15s ease",
};

export const WorkspaceSelector = ({
  workspaces = [],
  currentWorkspace,
  onSelect,
  onCreate,
  onReset,
  resetBusy = false,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const normalizedOptions = useMemo(() => {
    if (Array.isArray(workspaces) && workspaces.length > 0) {
      return workspaces;
    }
    if (currentWorkspace) {
      return [currentWorkspace];
    }
    return [];
  }, [workspaces, currentWorkspace]);

  const activeId =
    currentWorkspace?.id ||
    currentWorkspace?.clientId ||
    currentWorkspace?.workspaceId ||
    normalizedOptions[0]?.id;

  const activeName =
    currentWorkspace?.name ||
    currentWorkspace?.clientName ||
    normalizedOptions.find((opt) => opt.id === activeId)?.name ||
    activeId ||
    "default";

  useEffect(() => {
    const handleClick = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const handleSelect = (option) => {
    onSelect?.(option);
    setOpen(false);
  };

  const handleCreate = () => {
    setOpen(false);
    onCreate?.();
  };

  const handleReset = () => {
    setOpen(false);
    onReset?.();
  };

  const renderOptions = () => {
    if (!normalizedOptions.length) {
      return (
        <div
          style={{
            padding: "0.75rem 1rem",
            fontSize: "12px",
            color: "#9ca3af",
          }}
        >
          No workspaces found
        </div>
      );
    }
    return normalizedOptions.map((workspace) => {
      const optionId = workspace.id || workspace.clientId;
      const optionName =
        workspace.name || workspace.clientName || optionId || "Workspace";
      const isActive = optionId === activeId;
      return (
        <button
          key={optionId}
          type="button"
          onClick={() => handleSelect({ id: optionId, name: optionName })}
          style={optionButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#15151a";
            e.currentTarget.style.color = "#ffffff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#f4f4f5";
          }}
        >
          <span>{optionName}</span>
          {isActive && <Check size={16} color="#ec4899" />}
        </button>
      );
    });
  };

  return (
    <div
      ref={containerRef}
      style={{ position: "relative", display: "inline-flex", zIndex: 2600 }}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          ...baseButtonStyle,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          border: open ? "1px solid #6366f1" : baseButtonStyle.border,
          boxShadow: open
            ? "0 0 0 2px rgba(99,102,241,0.45)"
            : "none",
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.borderColor = "#4c4cff";
            e.currentTarget.style.boxShadow = "0 0 0 1px #4c4cff";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "#2b2b2f";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <FolderOpen size={16} color="#a1a1aa" />
        <span style={{ textTransform: "none" }}>{activeName}</span>
        <ChevronDown
          size={16}
          color="#a1a1aa"
          style={{
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>
      {open && (
        <div style={dropdownStyle}>
          <div>{renderOptions()}</div>
          {onReset && (
            <div
              style={{
                borderTop: "1px solid #27272a",
              }}
            >
              <button
                type="button"
                disabled={resetBusy}
                style={{
                  ...footerButtonStyle,
                  justifyContent: "flex-start",
                  gap: "0.5rem",
                  color: resetBusy ? "#f87171" : "#fda4af",
                  fontWeight: 600,
                  opacity: resetBusy ? 0.7 : 1,
                }}
                onClick={handleReset}
                onMouseEnter={(e) => {
                  if (!resetBusy) {
                    e.currentTarget.style.color = "#fecdd3";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = resetBusy ? "#f87171" : "#fda4af";
                }}
              >
                {resetBusy ? "Resetting memory…" : "Reset workspace memory"}
              </button>
            </div>
          )}
          <div
            style={{
              borderTop: "1px solid #27272a",
            }}
          >
            <button
              type="button"
              style={footerButtonStyle}
              onClick={handleCreate}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#e4e4e7";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#a1a1aa";
              }}
            >
              <span>Create workspace</span>
              <ArrowRight size={14} color="#71717a" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSelector;
